
import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Mail, MessageSquare, MapPin, Send, Instagram, Facebook, Youtube } from 'lucide-react';
import { useAccessibility } from '../hooks/useAccessibility';
import { ScrollReveal } from '../components/ScrollReveal';

const FaleConosco = () => {
  const { speak } = useAccessibility();
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    tipoSolicitacao: '',
    mensagem: ''
  });

  const tiposSolicitacao = [
    { value: '', label: 'Selecione o tipo de solicitação' },
    { value: 'suporte', label: 'Suporte técnico' },
    { value: 'sugestao', label: 'Sugestão de melhoria' },
    { value: 'parceria', label: 'Parceria' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'duvida', label: 'Dúvida geral' },
    { value: 'outro', label: 'Outro' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    speak('Formulário enviado com sucesso');
    // Aqui seria implementada a lógica de envio
    console.log('Dados do formulário:', formData);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-16 pb-14 md:pt-20 md:pb-16 bg-background overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.35] dark:opacity-[0.12]"
          style={{ backgroundImage: 'radial-gradient(rgb(var(--warm)) 1px, transparent 1px)', backgroundSize: '26px 26px' }}
          aria-hidden="true"
        />
        <div
          className="absolute -top-24 -left-24 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgb(var(--warm)) 0%, transparent 70%)' }}
          aria-hidden="true"
        />

        <div className="relative container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-warm/10 dark:bg-warm/15 px-3 py-1.5 rounded-full text-sm font-semibold text-foreground mb-6">
              <MessageSquare className="w-4 h-4 text-warm-ink dark:text-warm" />
              Fale Conosco
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-[1.1]">
              Estamos aqui para <span className="text-warm-ink dark:text-warm">ajudar</span>
            </h1>

            <p className="font-body text-lg md:text-xl text-muted-foreground leading-relaxed">
              Tem alguma dúvida, sugestão ou precisa de suporte?
              Nossa equipe está pronta para atendê-lo da melhor forma possível.
            </p>
          </div>
        </div>
      </section>

      <ScrollReveal>
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Informações de Contato */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-6">
                  Informações de Contato
                </h2>
                <p className="text-muted-foreground mb-8">
                  Entre em contato conosco através dos canais abaixo ou use o formulário ao lado.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">E-mail</h3>
                    <a href="mailto:gp.ticam@ifap.edu.br" className="text-muted-foreground hover:text-primary transition-colors">
                      gp.ticam@ifap.edu.br
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Endereço</h3>
                    <p className="text-muted-foreground">
                      BR-210, Km 03 - S/n - Brasil Novo<br />
                      Macapá - AP, 68909-398<br />
                      IFAP Campus Macapá
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Instagram</h3>
                    <a
                      href="https://www.instagram.com/gpticam/"
                      target="_blank"
                      rel="noreferrer"
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      @gpticam
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Facebook className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Facebook</h3>
                    <a
                      href="https://www.facebook.com/GPTICAM"
                      target="_blank"
                      rel="noreferrer"
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      GPTICAM
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Youtube className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">YouTube</h3>
                    <a
                      href="https://www.youtube.com/@Conectar-y9o"
                      target="_blank"
                      rel="noreferrer"
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      Conectar
                    </a>
                  </div>
                </div>
              </div>

              {/* Horários de Atendimento */}
              <div className="rounded-[1.75rem] bg-primary/[0.05] dark:bg-primary/[0.1] p-6">
                <h3 className="font-semibold text-foreground mb-4">Horários de Atendimento</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Segunda a Sexta:</span>
                    <span className="text-foreground">9h às 18h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sábado:</span>
                    <span className="text-foreground">9h às 14h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Domingo:</span>
                    <span className="text-foreground">Fechado</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Formulário */}
            <div className="rounded-[2rem] bg-warm/[0.06] dark:bg-warm/[0.11] p-8">
              <h2 className="text-2xl font-bold text-foreground mb-6">
                Envie sua mensagem
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="nome" className="block text-sm font-medium text-foreground mb-2">
                    Nome completo *
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Seu nome completo"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="tipoSolicitacao" className="block text-sm font-medium text-foreground mb-2">
                    Tipo de solicitação *
                  </label>
                  <select
                    id="tipoSolicitacao"
                    name="tipoSolicitacao"
                    value={formData.tipoSolicitacao}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {tiposSolicitacao.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="mensagem" className="block text-sm font-medium text-foreground mb-2">
                    Mensagem *
                  </label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    value={formData.mensagem}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    placeholder="Descreva sua dúvida, sugestão ou solicitação..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  onClick={() => speak('Enviando mensagem')}
                >
                  <Send className="w-5 h-5" />
                  <span>Enviar Mensagem</span>
                </button>
              </form>

              <div className="mt-6 p-4 bg-primary/5 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  * Campos obrigatórios. Suas informações são tratadas com total confidencialidade 
                  conforme nossa política de privacidade.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* CTA Rápido */}
      <ScrollReveal>
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Precisa de ajuda urgente?
          </h2>
          <p className="text-muted-foreground mb-6">
            Mande um e-mail que respondemos o quanto antes
          </p>
          <a
            href="mailto:gp.ticam@ifap.edu.br"
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center space-x-2"
            onClick={() => speak('Enviar e-mail para o GPTICAM')}
          >
            <Mail className="w-5 h-5" />
            <span>Enviar e-mail</span>
          </a>
        </div>
      </section>
      </ScrollReveal>

      <Footer />
    </div>
  );
};

export default FaleConosco;
